import Link from "next/link";
import dynamic from "next/dynamic";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bot } from "lucide-react";
import { useI18n } from "@/i18n";

const ChatDemoPlayer = dynamic(
  () => import("@/components/remotion/ChatDemoPlayer").then((mod) => mod.ChatDemoPlayer),
  { ssr: false }
);

export function HeroSection() {
  const { t } = useI18n();

  return (
    <section className="w-full bg-[#f8f8f8]">
      <div className="mx-auto max-w-[1152px] px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div className="flex flex-col gap-6">
            <Badge className="w-fit bg-[#f5f5f5] text-[#7a818e] border border-[#e5e5e5] hover:bg-[#f5f5f5]">
              <Bot className="size-3" />
              AI Agent Monetization
            </Badge>

            <h1 className="text-4xl sm:text-5xl md:text-[72px] font-medium tracking-[-1.8px] text-[#0a1733] leading-[1.1]">
              Give your <span className="text-[#ff4a01]">OpenClaw</span> the power to charge.
            </h1>

            <p className="text-lg text-[#7a818e] max-w-xl leading-relaxed">
              {t.home.heroSubtitleV2}
            </p>

            <div className="flex flex-wrap gap-3 mt-2">
              <Button asChild size="lg" className="bg-[#ff4a01] text-white hover:bg-[#e54400] border-0 rounded-[10px]">
                <Link href="/dashboard/api-keys">
                  {t.home.getApiKey}
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5] hover:text-[#0a1733] rounded-[10px]">
                <Link href="/seller">
                  {t.home.createPayLink}
                </Link>
              </Button>
            </div>

            <div className="flex items-center flex-wrap gap-x-5 gap-y-2 mt-1">
              <span className="text-sm text-[#b0b4ba]">Powered by</span>
              <div className="flex items-center gap-4">
                <a href="https://base.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#b0b4ba] hover:text-[#7a818e] transition-colors">
                  <svg className="shrink-0" width="20" height="20" viewBox="0 0 111 111" fill="none"><circle cx="55.5" cy="55.5" r="55.5" fill="#b0b4ba" /><path d="M55.3 91.7c20.1 0 36.4-16.3 36.4-36.4S75.4 18.9 55.3 18.9c-18.8 0-34.3 14.3-36.2 32.6h48v7.7h-48c1.9 18.2 17.4 32.5 36.2 32.5z" fill="#fff" /></svg>
                  <span className="text-xs font-medium">Base</span>
                </a>
                <a href="https://www.circle.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#b0b4ba] hover:text-[#7a818e] transition-colors">
                  <svg className="shrink-0" width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#b0b4ba" /><path d="M20.5 18.5c0-2.1-1.3-2.8-3.8-3.1-1.8-.3-2.2-.6-2.2-1.4 0-.7.5-1.2 1.6-1.2 1 0 1.5.3 1.8 1.1.1.2.2.3.4.3h1c.2 0 .4-.2.3-.4-.2-1.2-1-2.1-2.3-2.4v-1.4c0-.2-.2-.4-.4-.4h-.9c-.2 0-.4.2-.4.4v1.4c-1.6.3-2.6 1.4-2.6 2.8 0 2 1.2 2.7 3.7 3 1.7.3 2.3.6 2.3 1.5s-.7 1.4-1.8 1.4c-1.4 0-1.9-.6-2.1-1.4-.1-.2-.2-.3-.4-.3h-1c-.2 0-.4.2-.3.4.3 1.4 1.2 2.4 2.8 2.7v1.4c0 .2.2.4.4.4h.9c.2 0 .4-.2.4-.4v-1.4c1.7-.3 2.6-1.4 2.6-2.9z" fill="#fff" /><path d="M12.8 24.3c-4.6-1.7-7-6.8-5.3-11.4.9-2.3 2.7-4.1 5-5v-1.7c-3.2.9-5.7 3.4-6.8 6.5-2 5.7 1 12 6.8 14v-1.7c-.1 0-.1 0 0 0 .1-.3.2-.5.3-.7zM19.2 6.2v1.7c4.6 1.7 7 6.8 5.3 11.4-.9 2.3-2.7 4.1-5 5v1.7c3.2-.9 5.7-3.4 6.8-6.5 2-5.8-1-12-6.8-14-.1 0-.2 0-.3.1v.6z" fill="#fff" /></svg>
                  <span className="text-xs font-medium">Circle</span>
                </a>
                <a href="https://ethereum.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#b0b4ba] hover:text-[#7a818e] transition-colors">
                  <svg className="shrink-0" width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#b0b4ba" /><path d="M16 4v8.9l7.5 3.3L16 4z" fill="#fff" fillOpacity=".6" /><path d="M16 4l-7.5 12.2 7.5-3.3V4z" fill="#fff" /><path d="M16 21.9v6.1l7.5-10.4-7.5 4.3z" fill="#fff" fillOpacity=".6" /><path d="M16 28v-6.1l-7.5-4.3L16 28z" fill="#fff" /><path d="M16 20.4l7.5-4.2L16 13v7.4z" fill="#fff" fillOpacity=".2" /><path d="M8.5 16.2l7.5 4.2V13l-7.5 3.2z" fill="#fff" fillOpacity=".6" /></svg>
                  <span className="text-xs font-medium">Ethereum</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right: Chat Demo Animation */}
          <div className="flex justify-center lg:justify-end">
            <div className="w-[300px] h-[442px] md:w-[380px] md:h-[560px]">
              <ChatDemoPlayer />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
